import React, { useMemo } from "react";
import useThemeMode from "../../hooks/useThemeMode";
import { getMarketingTheme } from "../../utils/marketingTheme";
import useI18n from "../../i18n/useI18n";

const AboutWhyHookora = () => {
  const { isDark } = useThemeMode();
  const { t } = useI18n();
  const colors = useMemo(() => getMarketingTheme(isDark), [isDark]);

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const cards = useMemo(
    () => [
      {
        title: tt("about.cards.connectedWorkflow.title", "One connected workflow"),
        text: tt(
          "about.cards.connectedWorkflow.text",
          "Hooks, titles, captions, hashtags, scripts, descriptions, and thumbnail text work better when they are created in a connected system."
        ),
      },
      {
        title: tt("about.cards.momentum.title", "Faster creative momentum"),
        text: tt(
          "about.cards.momentum.text",
          "Hookora helps creators spend less time stuck on wording and more time actually moving toward publish-ready content."
        ),
      },
      {
        title: tt("about.cards.packaging.title", "Cleaner content packaging"),
        text: tt(
          "about.cards.packaging.text",
          "Strong content is not just about ideas. It is also about how those ideas are presented, framed, and made easier to click or watch."
        ),
      },
      {
        title: tt("about.cards.growingCreators.title", "Useful for growing creators"),
        text: tt(
          "about.cards.growingCreators.text",
          "Hookora is designed to be helpful whether you are just starting out or already creating consistently and need a smoother workflow."
        ),
      },
    ],
    [t]
  );

  return (
    <section style={{ padding: "12px 20px 42px", background: colors.sectionBg }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "26px" }}>
          <h2
            style={{
              margin: "0 0 12px",
              color: colors.text,
              fontSize: "clamp(2rem, 4vw, 3rem)",
              lineHeight: "1.08",
            }}
          >
            {tt("about.whyTitle", "What makes Hookora useful")}
          </h2>
          <p
            style={{
              maxWidth: "760px",
              margin: "0 auto",
              color: colors.textMuted,
              fontSize: "16px",
              lineHeight: "1.7",
            }}
          >
            {tt(
              "about.whySubtitle",
              "Hookora is not meant to make creators feel replaced. It is meant to make creators feel sharper, faster, and more supported."
            )}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "18px",
          }}
        >
          {cards.map((card, index) => (
            <div
              key={card.title}
              style={{
                background: colors.cardBg,
                border: `1px solid ${colors.border}`,
                borderRadius: "22px",
                padding: "22px",
                boxShadow: colors.shadow,
                backdropFilter: isDark ? "blur(14px)" : "none",
              }}
            >
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "12px",
                  background:
                    index % 2 === 0
                      ? "linear-gradient(135deg, #2563eb, #7c3aed)"
                      : "linear-gradient(135deg, #16a34a, #059669)",
                  marginBottom: "16px",
                }}
              />
              <h3 style={{ margin: "0 0 10px", color: colors.text, fontSize: "20px" }}>
                {card.title}
              </h3>
              <p style={{ margin: 0, color: colors.textMuted, lineHeight: "1.75", fontSize: "15px" }}>
                {card.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutWhyHookora;